import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Download,
  Mail,
  Send,
  Share2,
  Copy,
  Check,
  Loader2,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import type { ParticipantRole, ReleaseRecord } from '@shared/types';
import { adminApi } from './api';
import { getTg } from './telegram';
import { useLang, type DictKey } from '../i18n';

interface ReleaseDetailProps {
  token: string;
  onClose: () => void;
}

const ROLE_DICT_KEY: Record<ParticipantRole, DictKey> = {
  model: 'role.model',
  makeup_artist: 'role.makeup_artist',
  stylist: 'role.stylist',
  retoucher: 'role.retoucher',
  assistant: 'role.assistant',
  other: 'role.contributor',
};

const ReleaseDetail: React.FC<ReleaseDetailProps> = ({ token, onClose }) => {
  const { t, lang } = useLang();
  const [rec, setRec] = useState<ReleaseRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [copied, setCopied] = useState(false);
  const [resendLinkBusy, setResendLinkBusy] = useState(false);
  const [resendLinkOk, setResendLinkOk] = useState(false);

  const [emailTo, setEmailTo] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{ ok: boolean; to: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .getRelease(token)
      .then((r) => {
        if (cancelled) return;
        setRec(r);
        setEmailTo(r.photographer.email);
      })
      .catch((e) => !cancelled && setErr(e instanceof Error ? e.message : t('common.error')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const signUrl = `${window.location.origin}/sign/${token}`;
  const pdfUrl = `/api/releases/${token}/pdf`;

  const copySignUrl = () => {
    navigator.clipboard.writeText(signUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaTelegram = () => {
    const tg = getTg();
    const url = encodeURIComponent(signUrl);
    const text = encodeURIComponent(
      rec ? `Please sign the release for "${rec.shoot.title}"` : '',
    );
    const shareUrl = `https://t.me/share/url?url=${url}&text=${text}`;
    if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
    else window.open(shareUrl, '_blank');
  };

  const resendLink = async () => {
    setResendLinkBusy(true);
    try {
      const res = await adminApi.resendLink(token);
      setResendLinkOk(res.sentToChat);
    } finally {
      setResendLinkBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!emailTo) return;
    setEmailBusy(true);
    setEmailStatus(null);
    try {
      const res = await adminApi.sendEmail(token, emailTo);
      setEmailStatus({ ok: res.sent, to: res.to });
    } finally {
      setEmailBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] text-gray-400">
        <Loader2 className="animate-spin mr-2" size={16} /> {t('common.loading')}
      </div>
    );
  }
  if (err || !rec) {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <button onClick={onClose} className="text-indigo-600 text-sm mb-3 flex items-center gap-1">
          <ArrowLeft size={14} /> {t('common.back')}
        </button>
        <p className="text-red-600 text-sm">{err ?? t('detail.notFound')}</p>
      </div>
    );
  }

  const signed = !!rec.signedAt && !!rec.signer;
  const roleLabel = t(ROLE_DICT_KEY[rec.role]);
  const locale = lang === 'ru' ? 'ru-RU' : 'en-GB';

  return (
    <div className="max-w-2xl mx-auto p-4 pb-20">
      <button
        onClick={onClose}
        className="text-indigo-600 hover:text-indigo-700 text-sm mb-3 flex items-center gap-1 font-medium"
      >
        <ArrowLeft size={14} /> {t('detail.backToHistory')}
      </button>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white">
          <div className="flex items-start gap-3 mb-3">
            <h1 className="text-2xl font-bold serif text-gray-900 flex-1 leading-tight">
              {rec.shoot.title}
            </h1>
            <span
              className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full flex items-center gap-1 ${
                signed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}
            >
              {signed ? <CheckCircle2 size={11} /> : <Clock size={11} />}
              {signed ? t('status.signed') : t('status.pending')}
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <Cell label={t('common.role')} value={roleLabel} />
            <Cell label={t('create.field.date')} value={rec.shoot.date} />
            <Cell label={t('create.field.location')} value={rec.shoot.location || '—'} />
            <Cell
              label={t('common.created')}
              value={new Date(rec.createdAt).toLocaleString(locale)}
            />
            {rec.signedAt && (
              <Cell
                label={t('common.signed')}
                value={new Date(rec.signedAt).toLocaleString(locale)}
                full
              />
            )}
          </dl>
        </div>

        {signed && rec.signer && (
          <div className="p-5 border-b border-gray-100">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
              {t('detail.signer')}
            </h2>
            <dl className="text-sm space-y-1.5">
              <Row label={t('detail.field.name')} value={rec.signer.fullName} />
              <Row label={t('detail.field.email')} value={rec.signer.email} />
              <Row label={t('detail.field.phone')} value={rec.signer.phone} />
              <Row label={t('detail.field.dob')} value={rec.signer.dob} />
              <Row label={t('detail.field.address')} value={rec.signer.address} />
            </dl>
          </div>
        )}

        <div className="p-5 space-y-4">
          {!signed ? (
            <>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {t('detail.section.shareLink')}
              </h2>
              <input
                type="text"
                readOnly
                value={signUrl}
                className="w-full text-xs p-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 truncate font-mono"
              />
              <div className="grid grid-cols-3 gap-2">
                <Btn onClick={copySignUrl}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? t('common.copied') : t('detail.action.toMyChat')
                    .substring(0, 0) || t('common.copy')}
                </Btn>
                <Btn onClick={shareViaTelegram}>
                  <Share2 size={14} /> Telegram
                </Btn>
                <Btn onClick={resendLink} disabled={resendLinkBusy} primary>
                  <Send size={14} />
                  {resendLinkBusy
                    ? '…'
                    : resendLinkOk
                      ? t('detail.action.toMyChatSent')
                      : t('detail.action.toMyChat')}
                </Btn>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {t('detail.section.signedPdf')}
              </h2>
              <div className="grid grid-cols-2 gap-2">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg text-sm font-semibold hover:opacity-95 flex items-center justify-center gap-2 shadow-md shadow-indigo-200"
                >
                  <Download size={14} /> {t('detail.action.downloadPdf')}
                </a>
                <button
                  onClick={shareViaTelegram}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2"
                >
                  <Share2 size={14} /> {t('detail.action.shareTelegram')}
                </button>
              </div>

              <div className="pt-4 border-t border-gray-100">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                  {t('detail.section.sendByEmail')}
                </h2>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder={t('detail.email.placeholder')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                  />
                  <button
                    onClick={sendEmail}
                    disabled={emailBusy || !emailTo}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:bg-gray-300 transition flex items-center gap-1.5"
                  >
                    <Mail size={14} /> {emailBusy ? '…' : t('detail.action.sendEmail')}
                  </button>
                </div>
                {emailStatus && (
                  <p
                    className={`text-xs mt-1.5 ${
                      emailStatus.ok ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {emailStatus.ok
                      ? `${t('detail.email.success')} ${emailStatus.to}`
                      : `${t('detail.email.failed')} ${emailStatus.to}`}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Cell: React.FC<{ label: string; value: string; full?: boolean }> = ({ label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <dt className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</dt>
    <dd className="text-gray-900">{value}</dd>
  </div>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-3">
    <dt className="text-gray-500 w-24 shrink-0">{label}</dt>
    <dd className="text-gray-900 break-words flex-1">{value || '—'}</dd>
  </div>
);

const Btn: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  primary?: boolean;
  disabled?: boolean;
}> = ({ onClick, children, primary, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`py-2.5 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 ${
      primary
        ? 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-300'
        : 'bg-gray-100 hover:bg-gray-200 text-gray-800 disabled:bg-gray-50 disabled:text-gray-400'
    }`}
  >
    {children}
  </button>
);

export default ReleaseDetail;
